import { useContext, useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import LogoWhite from "../../assets/scss/black-dashboard-react/panther/logo-white.svg";
import clienteAxios from "../../config/axios";
import AuthContext from "../../context/autenticacion/authContext";

import Spinner from "components/spinners/Spinner";
import { FC_targetas } from "./functions";
import DetailsCardModal from "components/Modales/DetailsCardModal";
import FormEdit from "./FormEdit";
import Transfer from "./Transfer";
import ListaTarjetas from "components/Tarjetas/ListaTarjetas";
import Mensaje from "components/Alertas/Mensaje";
import "components/mercado/mis.css";
import { useT } from "i18n";

// Animaciones
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
`;

// Styled Components
const TabBar = styled.div`
  display: flex;
  gap: 0.5rem;
  background: rgba(12,14,19, 0.4);
  border-radius: 60px;
  padding: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const TabItem = styled.button`
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.active 
    ? 'rgba(227,168,43,.14)'
    : 'transparent'};
  color: ${props => props.active ? '#e3a82b' : 'rgba(255, 255, 255, 0.7)'};
  
  &:hover {
    transform: translateY(-2px);
    color: white;
    background: ${props => props.active 
      ? 'rgba(227,168,43,.14)'
      : 'rgba(227,168,43, 0.2)'};
  }
  
  i {
    margin-right: 0.5rem;
  }
`;

const CardGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const CardItem = styled.div`
  background: linear-gradient(135deg, rgba(12,14,19, 0.8), rgba(12,14,19, 0.9));
  border-radius: 16px;
  border: 1px solid rgba(227,168,43, 0.2);
  padding: 1rem;
  transition: all 0.3s ease;
  animation: ${slideIn} 0.3s ease;
  
  &:hover {
    transform: translateX(5px);
    border-color: rgba(227,168,43, 0.4);
    box-shadow: 0 4px 15px rgba(227,168,43, 0.1);
  }
`;

const CardRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr auto;
  gap: 1rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    text-align: center;
  }
`;

const CardLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  i {
    color: #e3a82b;
    font-size: 1rem;
  }
  
  strong {
    color: #fff;
    font-size: 0.85rem;
  }
`;

const CardValue = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.85rem;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${props => props.status === 'APROBADO' 
    ? 'rgba(227,168,43, 0.15)' 
    : props.status === 'PENDIENTE' 
      ? 'rgba(255, 193, 7, 0.15)'
      : 'rgba(255, 82, 82, 0.15)'};
  color: ${props => props.status === 'APROBADO' 
    ? '#e3a82b' 
    : props.status === 'PENDIENTE'
      ? '#ffc107'
      : '#ff5252'};
`;

const BalanceValue = styled.strong`
  font-size: 1rem;
  color: #e3a82b;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const ActionButton = styled.button`
  background: rgba(227,168,43, 0.2);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: #e3a82b;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(227,168,43, 0.4);
    transform: scale(1.05);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 50px;
  border: none;
  background: #e3a82b;
  color: #14100a;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(227,168,43, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const PaginationInfo = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  
  strong {
    color: #e3a82b;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #0c0e13, #0c0e13);
  border-radius: 24px;
  border: 1px solid rgba(227,168,43, 0.3);
  max-width: 450px;
  width: 90%;
  padding: 2rem;
  text-align: center;
  
  img {
    width: 80px;
    height: auto;
    margin-bottom: 1rem;
  }
  
  h2 {
    font-size: 1.2rem;
    margin-bottom: 1rem;
  }
  
  h4 {
    font-size: 1rem;
    margin-bottom: 1rem;
    color: #e3a82b;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background: rgba(12,14,19, 0.4);
  border-radius: 16px;
  border: 1px solid rgba(227,168,43, 0.2);
  
  i {
    font-size: 3rem;
    color: rgba(255, 255, 255, 0.3);
    margin-bottom: 1rem;
  }
  
  p {
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 1rem;
  }
`;

const EmptyButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  border: none;
  background: #e3a82b;
  color: #14100a;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(227,168,43, 0.4);
  }
  
  i {
    margin-right: 0.5rem;
    font-size: 0.9rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: 1px solid rgba(227,168,43, 0.3);
  border-radius: 50px;
  padding: 0.5rem 1rem;
  color: #e3a82b;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1.5rem;
  
  &:hover {
    background: rgba(227,168,43, 0.1);
    border-color: #e3a82b;
  }
  
  i {
    font-size: 0.9rem;
  }
`;

const UserTargetas = (props) => {
  const { t, ts } = useT();
  const [selectedCard, setSelectedCard] = useState(null);
  const [openModalDisabledCard, setOpenModalDisabledCard] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [btnDownloadXML, setBtnDownloadXML] = useState(false);
  const [btnDownloadPDF, setBtnDownloadPDF] = useState(false);

  const authContext = useContext(AuthContext);
  const { usuario } = authContext;

  const [isLoading, setIsLoading] = useState(true);
  const [handleCountries, setHandleCountries] = useState([]);
  const [tarjetas, setTarjetas] = useState([]);
  const [dataUser, setDataUser] = useState(usuario);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState([]);
  const [reason, setReason] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [back, setback] = useState(0);
  const [prev, setPrev] = useState(5);
  const [count, setCount] = useState(1);
  const [exportFile, setExportFile] = useState(false);
  const [idFile, setIdFile] = useState(null);
  const [countPage, setCountPage] = useState(null);
  const [wait, setWait] = useState(false);

  const [detailsTarjeta, setDetailsTarjeta] = useState({ primero: 1, segundo: 0 });
  const [appbar, guardarAppBar] = useState({ primero: 1, segundo: 0, tercero: 0 });

  const handleModalDisableCard = (id) => {
    const infoDelete = tarjetas.filter((info) => info.id === id)[0];
    setInfo(infoDelete);
    setOpenModalDisabledCard(true);
  };

  const handleShowDetails = (id) => {
    setSelectedCard(id);
    setDetailsTarjeta({ primero: 1, segundo: 0 });
    guardarAppBar({ ...appbar, primero: 0, segundo: 0, tercero: 0 });
  };

  const handleCloseDetails = () => {
    setSelectedCard(null);
    setDetailsTarjeta({ primero: 0, segundo: 1 });
    guardarAppBar({ ...appbar, primero: 1, segundo: 0, tercero: 0 });
  };

  const ActivarPrimero = () => guardarAppBar({ ...appbar, primero: 1, segundo: 0, tercero: 0 });
  const ActivarSegundo = () => guardarAppBar({ ...appbar, primero: 0, segundo: 1, tercero: 0 });
  const ActivarTercero = () => guardarAppBar({ ...appbar, primero: 0, segundo: 0, tercero: 1 });

  useEffect(() => {
    const consultarTargetas = async () => {
      try {
        const respuesta = await clienteAxios.post("/api/card/list");
        const tt = respuesta.data.data.length;
        const total = Math.ceil(tt / 5);
        setCountPage(total);
        setTarjetas(respuesta.data.data);
        setIsLoading(false);

        const countries = await clienteAxios.post("/api/paises/list");
        setHandleCountries(countries.data.data);
      } catch (error) {
        console.error(error);
        setIsLoading(false);
      }
    };
    consultarTargetas();
  }, []);

  const [profilechange, guardarProfileChange] = useState({
    first_name: "", last_name: "", document_type: "", cedula: "",
    email: "", birthDate: "", phone: "", genero: "", estadocivil: "",
    nacionalidad: "", address: "", state: "", country_id: "", city: "",
    zipCode: "", profesion: "",
  });

  const onChange = (e) => {
    guardarProfileChange({ ...profilechange, [e.target.name]: e.target.value });
  };

  const mount = tarjetas.length;
  // Transferir entre tarjetas solo tiene sentido con 2 o más tarjetas
  const puedeTransferir = tarjetas.length >= 2;
  const handleBack = () => {
    if (back >= 5) {
      setback(back - 5);
      setPrev(prev - 5);
      setCount(count - 1);
    }
  };

  const handleNext = () => {
    if (prev < mount) {
      setback(back + 5);
      setPrev(prev + 5);
      setCount(count + 1);
    }
  };

  const handleDeleteCard = async (card_id, reason) => {
    const data = { card_id, reason };
    setWait(true);
    try {
      const res = await clienteAxios.post("/api/card/change", data);
      if (res.data.success) {
        setOpenModalDisabledCard(false);
        setSuccessMessage(t("tarjeta.cancelada"));
        setShowMessage(true);
        setWait(false);
        const respuesta = await clienteAxios.post("/api/card/list");
        setTarjetas(respuesta.data.data);
      }
    } catch (error) {
      setOpenModalDisabledCard(false);
      setSuccessMessage(ts(error.response?.data?.message) || t("tarjeta.noCancelada"));
      setShowMessage(true);
      setWait(false);
    }
  };

  const handleExportFiles = (id) => {
    setIdFile(id);
    setExportFile(true);
  };

  const handleLink = async (link) => {
    window.open(link, '_blank');
  };

  const handleExportXML = (id, type, from, to) => {
    tarjetas.filter((item) => item.id === id).map(async (tar) => {
      if (tar.movimientos?.length === 0) return null;
      setBtnDownloadXML(true);
      const data = { card_id: id, type, from, to };
      const res = await clienteAxios.post("/api/card/movements", data);
      if (res.data.success) {
        setTimeout(async () => {
          setBtnDownloadXML(false);
          await handleLink(res.data.data);
        }, 1000);
      }
    });
  };

  const handleExportPDF = (id, type, from, to) => {
    tarjetas.filter((item) => item.id === id).map(async (tar) => {
      if (tar.movimientos?.length === 0) return null;
      setBtnDownloadPDF(true);
      const data = { card_id: id, type, from, to };
      const res = await clienteAxios.post("/api/card/movements", data);
      if (res.data.success) {
        setTimeout(async () => {
          setBtnDownloadPDF(false);
          await handleLink(res.data.data);
        }, 1000);
      }
    });
  };

  const handleUpdate = async (updateData) => {
    const firstName = profilechange.first_name || updateData?.name?.split(" ")[0] || "";
    const lastName = profilechange.last_name || updateData?.name?.split(" ")[1] || "";
    const document_type = profilechange.document_type || updateData?.document_type || "Cédula/DNI";
    const cedula = profilechange.cedula || updateData?.identity || "";
    const email = profilechange.email || updateData?.email || "";
    const birthDate = profilechange.birthDate || updateData?.birthday_date || "";
    const phone = profilechange.phone || updateData?.telephone || "";
    const genero = profilechange.genero || updateData?.genero || "";
    const estadocivil = profilechange.estadocivil || updateData?.estadocivil || "";
    const nacionalidad = profilechange.nacionalidad || updateData?.address_country || "";
    const calle = profilechange.address_street ?? updateData?.address_street ?? "";
    const complemento = profilechange.address_number ?? updateData?.address_number ?? "";
    const address = [calle, complemento].map((x) => String(x || "").trim()).filter(Boolean).join(", ");
    const state = profilechange.state || updateData?.address_state || "";
    const city = profilechange.city || updateData?.address_city || "";
    const zipCode = profilechange.zipCode || updateData?.address_zipcode || "";
    const profesion = profilechange.profesion || updateData?.comercial_name || "";
    const country_id = profilechange.country_id || updateData?.address_country || "";

    const data = { first_name: firstName, last_name: lastName, document_type, cedula, email, birthDate, phone, genero, estadocivil, nacionalidad, address, state, country_id, city, zipCode, profesion };
    const ETIQUETAS = { first_name: t("tarjeta.lblNombre"), last_name: t("tarjeta.lblApellido"), document_type: t("tarjeta.lblTipoDoc"), cedula: t("tarjeta.lblNumDoc"), email: t("tarjeta.lblCorreo"), birthDate: t("tarjeta.lblNacimiento"), phone: t("tarjeta.lblTelefono"), genero: t("tarjeta.lblSexo"), estadocivil: t("tarjeta.lblEstadoCivil"), nacionalidad: t("tarjeta.lblNacionalidad"), address: t("tarjeta.lblCalle"), state: t("tarjeta.lblProvincia"), country_id: t("tarjeta.lblPaisRes"), city: t("tarjeta.lblCiudad"), zipCode: t("tarjeta.lblPostal"), profesion: t("tarjeta.lblProfesion") };
    const emptyFields = Object.keys(data).filter((key) => !data[key]);

    if (emptyFields.length > 0) {
      setErrorMessage(t("tarjeta.completaCampos", { campos: emptyFields.map((k) => ETIQUETAS[k] || k).join(", ") }));
      setEditing(true);
      setTimeout(() => setEditing(false), 3000);
    } else {
      setLoading(true);
      try {
        const res = await clienteAxios.post("/api/card/create", data);
        if (res.data.success) {
          setErrorMessage(t("tarjeta.datosActualizados"));
          setEditing(true);
          setLoading(false);
          setTimeout(async () => {
            const dataUpdate = await clienteAxios.post("/api/card/list");
            setTarjetas(dataUpdate.data.data);
            setEditing(false);
          }, 2000);
        }
      } catch (error) {
        setErrorMessage(ts(error.response?.data?.message) || t("tarjeta.errorActualizar"));
        setEditing(true);
        setLoading(false);
      }
    }
  };

  // Función para volver al listado
  const volverAListado = () => {
    guardarAppBar({ ...appbar, primero: 1, segundo: 0, tercero: 0 });
    setSelectedCard(null);
    setEditing(false);
  };

  if (isLoading) {
    return (
      <div className="content">
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      {/* Mostrar botón volver cuando estamos viendo detalles de tarjeta */}
      {selectedCard !== null && detailsTarjeta.primero === 1 && (
        <BackButton onClick={handleCloseDetails}>
          <i className="tim-icons icon-minimal-left" /> {t("tarjeta.volverListado")}
        </BackButton>
      )}

      <TabBar>
        <TabItem active={appbar.primero === 1} onClick={ActivarPrimero}>
          <i className="tim-icons icon-credit-card" /> {t("tarjeta.tabLista")}
        </TabItem>
        <TabItem active={appbar.segundo === 1} onClick={ActivarSegundo}>
          <i className="tim-icons icon-single-02" /> {t("tarjeta.tabAgregar")}
        </TabItem>
        {puedeTransferir && (
          <TabItem active={appbar.tercero === 1} onClick={ActivarTercero}>
            <i className="tim-icons icon-send" /> {t("tarjeta.tabTransferir")}
          </TabItem>
        )}
      </TabBar>

      {/* Formulario para agregar tarjeta */}
      {appbar.segundo === 1 && (
        <FormEdit
          data={dataUser}
          onUpdate={handleUpdate}
          onDataChange={onChange}
          errorMessage={errorMessage}
          editState={editing}
          setEditState={() => setEditing(false)}
          loading={loading}
          onCancel={volverAListado}
        />
      )}

      {/* Detalles de tarjeta */}
      {selectedCard !== null && detailsTarjeta.primero === 1 && (
        <DetailsCardModal
          data={tarjetas.filter((detail) => detail.id === selectedCard)[0]}
          onClose={handleCloseDetails}
        />
      )}

      {/* Lista de tarjetas */}
      {appbar.primero === 1 && (
        <div className="position-relative">
          {openModalDisabledCard && (
            <div className="p7x-overlay" onClick={() => !wait && setOpenModalDisabledCard(false)}>
              <div className="p7x-modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
                <div className="p7x-modal-head">
                  <h3>{info?.number ? t("tarjeta.desactivarTitulo") : t("tarjeta.cancelarSolicitud")}</h3>
                </div>
                <p style={{ color: "#8b93a5" }}>
                  {info?.number
                    ? t("tarjeta.desactivarAviso")
                    : t("tarjeta.cancelarAviso")}
                </p>
                <div className="p7t-form">
                  <label htmlFor="motivo-tarjeta">{t("tarjeta.motivo")}</label>
                  <input id="motivo-tarjeta" type="text" className="p7x-in" value={reason} onChange={(e) => setReason(e.target.value)} />
                </div>
                <div className="p7t-pie-modal">
                  <button type="button" className="p7-btn-ghost" disabled={wait} onClick={() => setOpenModalDisabledCard(false)}>{t("tarjeta.volver")}</button>
                  <button type="button" className="p7-btn-gold" disabled={wait} onClick={() => handleDeleteCard(info.id, reason)}>
                    {wait ? t("tarjeta.procesando") : t("tarjeta.confirmar")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {exportFile && (
            <div className="p7x-overlay" onClick={() => setExportFile(false)}>
              <div className="p7x-modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
                <div className="p7x-modal-head"><h3>{t("tarjeta.descargarMov")}</h3></div>
                <div className="p7t-form">
                  <label htmlFor="mov-desde">{t("tarjeta.desde")}</label>
                  <input id="mov-desde" type="date" className="p7x-in" onChange={(e) => setDateFrom(e.target.value)} />
                  <label htmlFor="mov-hasta">{t("tarjeta.hasta")}</label>
                  <input id="mov-hasta" type="date" className="p7x-in" onChange={(e) => setDateTo(e.target.value)} />
                </div>
                <div className="p7t-pie-modal">
                  <button type="button" className="p7-btn-ghost" onClick={() => setExportFile(false)}>{t("tarjeta.cerrar")}</button>
                  <button type="button" className="p7-btn-ghost" disabled={btnDownloadXML} onClick={() => handleExportXML(idFile, "xls", dateFrom, dateTo)}>
                    {btnDownloadXML ? t("tarjeta.generando") : t("tarjeta.excel")}
                  </button>
                  <button type="button" className="p7-btn-gold" disabled={btnDownloadPDF} onClick={() => handleExportPDF(idFile, "pdf", dateFrom, dateTo)}>
                    {btnDownloadPDF ? t("tarjeta.generando") : t("tarjeta.pdf")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showMessage && (
            <Mensaje mensaje={successMessage} guardarMensajeOpen={setShowMessage} guardarMensaje={setSuccessMessage} />
          )}

          <ListaTarjetas
            tarjetas={tarjetas}
            onAgregar={() => guardarAppBar({ ...appbar, primero: 0, segundo: 1, tercero: 0 })}
            onDetalles={handleShowDetails}
            onExportar={handleExportFiles}
            onDesactivar={(id) => { setReason(""); handleModalDisableCard(id); }}
          />
        </div>
      )}

      {/* Transferencias */}
      {appbar.tercero === 1 && puedeTransferir && <Transfer />}
    </div>
  );
};

export default UserTargetas;